import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useRef, createContext, useState } from 'react';
import { getAuth, getConfig } from '../lib/allauth';
import { mark } from '../lib/boot/bootMetrics';
import {
  takeBootAuthRequest,
  takeBootConfigRequest,
} from '../lib/boot/bootRequests';
import { getBuildVersion } from '../lib/config';
import LoadingScreen from '../pages/LoadingPage';
import LoadError from '../pages/LoadError';

export const AuthContext = createContext(null);

// Only [200, 401, 410] are valid allauth auth status codes
const VALID_STATUSES = [200, 401, 410];

function buildAuthErrorTags(errorType) {
  return {
    auth_error_type: errorType,
    online: navigator.onLine,
    build_version: getBuildVersion() ?? 'unknown',
  };
}

function isValidAuthResponse(response) {
  if (
    !response ||
    typeof response !== 'object' ||
    typeof response.status !== 'number'
  ) {
    return false;
  }
  // Only accept known-valid status codes
  if (!VALID_STATUSES.includes(response.status)) {
    return false;
  }
  // For known status codes, data and meta can be undefined (graceful degradation)
  // but should not be explicitly null (indicates a response construction error)
  if (response.data === null || response.meta === null) {
    return false;
  }
  return true;
}

export function AuthContextProvider(props) {
  const [auth, setAuth] = useState(undefined);
  const [authError, setAuthError] = useState(null);
  const [config, setConfig] = useState(undefined);
  // Guard against StrictMode double-mount triggering duplicate fetches
  const fetchedRef = useRef(false);

  useEffect(() => {
    function onAuthChanged(e) {
      if (!isValidAuthResponse(e.detail)) {
        console.error('Invalid auth response received:', e.detail);
        // Known-status responses with invalid data (null data/meta) are auth errors;
        // everything else (missing status, non-numeric status, unknown status codes,
        // or completely malformed payloads) defaults to server error —
        // blame ourselves, not the user
        const hasKnownStatus =
          e.detail &&
          typeof e.detail.status === 'number' &&
          VALID_STATUSES.includes(e.detail.status);
        const errorType = hasKnownStatus ? 'auth' : 'server';
        setAuthError(errorType);
        setAuth(false);
        return;
      }
      setAuthError(null);
      setAuth(auth => {
        if (typeof auth === 'undefined') {
          console.log('Authentication status loaded');
        } else {
          console.log('Authentication status updated');
        }
        return e.detail;
      });
    }

    document.addEventListener('allauth.auth.change', onAuthChanged);

    // Only fetch once — StrictMode double-mounts this effect
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      // prefetch.ts starts the session and config requests at parse time on a
      // hard load; adopt them so the auth gate resolves as early as the
      // network allows. When nothing is pending (tests, booking hosts), fetch.
      const authRequest = takeBootAuthRequest() ?? getAuth();
      const configRequest = takeBootConfigRequest() ?? getConfig();
      authRequest
        .then(data => {
          if (!isValidAuthResponse(data)) {
            console.error('Invalid auth response from getAuth:', data);
            const hasKnownStatus =
              data &&
              typeof data.status === 'number' &&
              VALID_STATUSES.includes(data.status);
            const errorType = hasKnownStatus ? null : 'server';
            Sentry.captureException(
              new Error('Auth bootstrap failed: invalid getAuth response'),
              {
                tags: buildAuthErrorTags(errorType ?? 'auth'),
                extra: { response: data },
              }
            );
            setAuthError(errorType);
            setAuth(false);
            mark('auth:error', { kind: 'invalid-response' });
            return;
          }
          setAuthError(null);
          setAuth(data);
          mark('auth:resolved', { status: data.status });
        })
        .catch(e => {
          console.error(e);
          // A fetch TypeError (request never reached the server) or a browser that
          // reports itself offline means the failure is on the device's side of the
          // wire -> 'network'. A SyntaxError (non-JSON edge 5xx) or any other thrown
          // error means the server responded badly -> 'server'.
          const isNetwork =
            e instanceof TypeError || navigator.onLine === false;
          const errorType = isNetwork ? 'network' : 'server';
          Sentry.captureException(e, {
            tags: buildAuthErrorTags(errorType),
          });
          setAuthError(errorType);
          setAuth(false);
          mark('auth:error', { kind: errorType });
        });
      configRequest
        .then(data => setConfig(data))
        .catch(e => {
          console.error(e);
        });
    }
    return () => {
      document.removeEventListener('allauth.auth.change', onAuthChanged);
    };
  }, []);
  const loading = typeof auth === 'undefined';
  const contextValue = useMemo(() => ({ auth, config }), [auth, config]);
  return (
    <AuthContext.Provider value={contextValue}>
      {loading ? (
        <LoadingScreen />
      ) : auth === false ? (
        <LoadError
          errorType={authError ?? undefined}
          loginUrl={props.loginUrl}
        />
      ) : (
        props.children
      )}
    </AuthContext.Provider>
  );
}
